import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import RequireRole from "@/components/RequireRole";
import Login from "@/pages/Login";
import DashboardCommerce from "@/pages/DashboardCommerce";
import DashboardAdmin from "@/pages/DashboardAdmin";
import Prospects from "@/pages/Prospects";
import ProspectDetail from "@/pages/ProspectDetail";
import Dossiers from "@/pages/Dossiers";
import DossierDetail from "@/pages/DossierDetail";
import Denonciations from "@/pages/Denonciations";
import Options from "@/pages/Options";
import Financements from "@/pages/Financements";
import Notaire from "@/pages/Notaire";
import Factures from "@/pages/Factures";
import Recouvrement from "@/pages/Recouvrement";
import EmailTemplates from "@/pages/EmailTemplates";
import UsersPage from "@/pages/Users";
import Reports from "@/pages/Reports";
import Promoteurs from "@/pages/Promoteurs";
import PortailsPromoteurs from "@/pages/PortailsPromoteurs";
import EmailValidation from "@/pages/EmailValidation";
import Analytics from "@/pages/Analytics";
import MonCompte from "@/pages/MonCompte";
import SuckMyLead from "@/pages/SuckMyLead";
import EmailHistory from "@/pages/EmailHistory";
import AiQuotaRequests from "@/pages/AiQuotaRequests";
import PotCommun from "@/pages/PotCommun";
import RdvPlanifies from "@/pages/RdvPlanifies";
import Relances from "@/pages/Relances";
import Chat from "@/pages/Chat";

const COMMERCE = ["admin", "dirigeant", "manager_commercial", "vendeur"];
const ADMIN_BO = ["admin", "dirigeant", "secretaire_bo", "responsable_facturation"];
const ADMIN_BO_EXT = [...ADMIN_BO, "manager_commercial", "vendeur"];
const FACTURATION = ["admin", "dirigeant", "responsable_facturation"];
const BO_ONLY = ["admin", "dirigeant", "secretaire_bo"];
const ADMIN_DIR = ["admin", "dirigeant"];

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  const commerce = ["vendeur", "manager_commercial"];
  const adminBo = ["secretaire_bo", "responsable_facturation"];
  if (commerce.includes(user.role)) return <Navigate to="/commerce" replace />;
  if (adminBo.includes(user.role)) return <Navigate to="/admin" replace />;
  return <Navigate to="/commerce" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route element={<Layout />}>
            <Route path="/commerce" element={<RequireRole allowed={COMMERCE}><DashboardCommerce /></RequireRole>} />
            <Route path="/prospects" element={<RequireRole allowed={COMMERCE}><Prospects /></RequireRole>} />
            <Route path="/prospects/:id" element={<RequireRole allowed={COMMERCE}><ProspectDetail /></RequireRole>} />
            <Route path="/pot-commun" element={<RequireRole allowed={COMMERCE}><PotCommun /></RequireRole>} />
            <Route path="/rdv-planifies" element={<RequireRole allowed={COMMERCE}><RdvPlanifies /></RequireRole>} />
            <Route path="/relances" element={<RequireRole allowed={COMMERCE}><Relances /></RequireRole>} />

            <Route path="/admin" element={<RequireRole allowed={[...ADMIN_BO, "manager_commercial"]}><DashboardAdmin /></RequireRole>} />
            <Route path="/dossiers" element={<RequireRole allowed={ADMIN_BO_EXT}><Dossiers /></RequireRole>} />
            <Route path="/dossiers/:id" element={<RequireRole allowed={ADMIN_BO_EXT}><DossierDetail /></RequireRole>} />

            <Route path="/denonciations" element={<RequireRole allowed={BO_ONLY}><Denonciations /></RequireRole>} />
            <Route path="/options" element={<RequireRole allowed={BO_ONLY}><Options /></RequireRole>} />
            <Route path="/financements" element={<RequireRole allowed={BO_ONLY}><Financements /></RequireRole>} />
            <Route path="/notaire" element={<RequireRole allowed={BO_ONLY}><Notaire /></RequireRole>} />

            <Route path="/factures" element={<RequireRole allowed={FACTURATION}><Factures /></RequireRole>} />
            <Route path="/recouvrement" element={<RequireRole allowed={FACTURATION}><Recouvrement /></RequireRole>} />

            <Route path="/templates" element={<RequireRole allowed={ADMIN_BO}><EmailTemplates /></RequireRole>} />
            <Route path="/promoteurs" element={<RequireRole allowed={[...ADMIN_BO, "manager_commercial"]}><Promoteurs /></RequireRole>} />
            <Route path="/portails-promoteurs" element={<RequireRole allowed={ADMIN_BO_EXT}><PortailsPromoteurs /></RequireRole>} />
            <Route path="/validation-emails" element={<RequireRole allowed={[...ADMIN_BO, "manager_commercial"]}><EmailValidation /></RequireRole>} />
            <Route path="/analytics" element={<RequireRole allowed={["admin","dirigeant","manager_commercial","vendeur"]}><Analytics /></RequireRole>} />
            <Route path="/users" element={<RequireRole allowed={ADMIN_DIR}><UsersPage /></RequireRole>} />
            <Route path="/reports" element={<RequireRole allowed={["admin","dirigeant","manager_commercial"]}><Reports /></RequireRole>} />
            <Route path="/mon-compte" element={<MonCompte />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/suck-my-lead" element={<RequireRole allowed={["admin","dirigeant","manager_commercial"]}><SuckMyLead /></RequireRole>} />
            <Route path="/demandes-ia" element={<RequireRole allowed={["admin","dirigeant","manager_commercial"]}><AiQuotaRequests /></RequireRole>} />
            <Route path="/historique-emails" element={<RequireRole allowed={["admin","dirigeant","secretaire_bo","responsable_facturation","manager_commercial"]}><EmailHistory /></RequireRole>} />
          </Route>

          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
